import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, comics } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 12;

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      setCurrentPage(1);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const handleCategorySwitch = useCallback((newSlug: string) => {
    setActiveSlug(newSlug);
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const filtered =
    activeSlug === 'all'
      ? comics
      : comics.filter((c) => c.category === activeSlug);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const activeCategory = categories.find((c) => c.slug === activeSlug);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <section className="w-full bg-background-50 py-6 border-b border-background-200">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1 text-sm text-foreground-500 mb-4">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800 font-medium">
                {activeCategory ? activeCategory.name + '漫画' : '全部分类'}
              </span>
            </nav>

            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4">
              {activeCategory ? activeCategory.name + '漫画' : '全部韩国漫画'} - 韩漫免费在线观看
            </h1>

            <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => handleCategorySwitch(cat.slug)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-8 md:py-12 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <p className="text-sm text-foreground-500 mb-6">
              共找到 <strong className="text-foreground-800">{filtered.length}</strong> 部作品
            </p>

            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-5xl text-foreground-300"></i>
                <p className="mt-4 text-foreground-500">该分类暂无作品</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                  {paginated.map((comic) => (
                    <Link
                      key={comic.id}
                      to={`/article/${comic.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100 relative mb-3">
                        <img
                          src={comic.coverImage}
                          alt={comic.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                          title={`${comic.title} - ${activeCategory?.name || ''}韩国漫画`}
                          loading="lazy"
                        />
                        <div className="absolute top-2 right-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-black/60 text-white text-xs">
                            {comic.rating}
                          </span>
                        </div>
                        {comic.isHot && (
                          <div className="absolute top-2 left-2">
                            <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full bg-accent-500 text-white text-xs">
                              <i className="ri-fire-line"></i>热门
                            </span>
                          </div>
                        )}
                      </div>
                      <h3 className="text-sm md:text-base font-medium text-foreground-800 group-hover:text-primary-500 transition-colors truncate">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">
                        {comic.author} · {comic.chapters}话
                      </p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {comic.tags.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="inline-block px-2 py-0.5 rounded text-xs bg-secondary-100 text-secondary-700"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </Link>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          type="button"
                          onClick={() => {
                            setCurrentPage(page);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-all cursor-pointer ${
                            currentPage === page
                              ? 'bg-primary-500 text-white'
                              : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: `${
                activeCategory ? activeCategory.name + '漫画' : '全部韩国漫画'
              } - 韩漫免费在线观看`,
              description: `在线浏览${
                activeCategory ? activeCategory.name : '各种'
              }韩国漫画大全，韩漫网站为您提供免费下拉式漫画阅读体验。`,
              url: `https://manhwa-reader.example.com/category/${activeSlug}`,
              isPartOf: {
                '@type': 'WebSite',
                name: '韩漫阅读',
                url: 'https://manhwa-reader.example.com',
              },
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  {
                    '@type': 'ListItem',
                    position: 1,
                    name: '首页',
                    item: 'https://manhwa-reader.example.com/',
                  },
                  {
                    '@type': 'ListItem',
                    position: 2,
                    name: activeCategory ? activeCategory.name + '漫画' : '全部分类',
                    item: `https://manhwa-reader.example.com/category/${activeSlug}`,
                  },
                ],
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}